import { useState } from "react";
import TaskCard from "./TaskCard";

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
};

function ClientAvatar({ name, imageSrc }) {
  const [imgError, setImgError] = useState(false);
  if (!imageSrc || imgError) {
    return <span>{getInitials(name)}</span>;
  }
  return (
    <img
      src={imageSrc}
      alt={name}
      style={{
        width: 38, height: 38, borderRadius: "50%",
        objectFit: "cover", display: "block",
        margin: "0 auto", border: "1.5px solid #e2e8f0",
      }}
      onError={() => setImgError(true)}
    />
  );
}

/**
 * Assign tasks within a category to non-overlapping lanes (sub-rows).
 * Returns an array of lanes, each lane being an array of cell descriptors
 * that together cover exactly 5 columns (Mon–Fri).
 */
function buildLanes(categoryTasks) {
  const sorted = [...categoryTasks].sort((a, b) => a.startDay - b.startDay);
  // laneEnds[i] = next free day index for lane i
  const laneEnds = [];
  const lanes    = [];

  sorted.forEach((task) => {
    let placed = false;
    for (let li = 0; li < laneEnds.length; li++) {
      if (task.startDay >= laneEnds[li]) {
        lanes[li].push(task);
        laneEnds[li] = task.endDay + 1;
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push([task]);
      laneEnds.push(task.endDay + 1);
    }
  });

  // For each lane, expand to full 5-column cell list (task + empty gaps)
  return lanes.map((laneTasks) => {
    const cells = [];
    let cursor = 0;
    for (const task of laneTasks) {
      if (task.startDay > cursor)
        cells.push({ type: "empty", span: task.startDay - cursor, key: `empty-${cursor}-${task.id}` });
      cells.push({ type: "task", task, span: task.endDay - task.startDay + 1, key: `task-${task.id}` });
      cursor = task.endDay + 1;
    }
    if (cursor < 5)
      cells.push({ type: "empty", span: 5 - cursor, key: `empty-end-${cursor}` });
    return cells;
  });
}

const TH_CORNER = {
  background: "#f8fafc",
  borderBottom: "2px solid #e2e8f0",
  borderRight: "1px solid #e2e8f0",
  padding: "10px 8px",
  position: "sticky",
  left: 0,
  zIndex: 3,
  width: 70,
};

const TH_DAY = {
  background: "#f8fafc",
  borderBottom: "2px solid #e2e8f0",
  borderRight: "1px solid #e2e8f0",
  padding: "10px 16px",
  textAlign: "left",
};

const TD_LABEL = {
  fontSize: 11,
  fontWeight: 700,
  color: "#6c757d",
  letterSpacing: "0.06em",
  textAlign: "center",
  padding: "8px 4px",
  background: "#f8f9fa",
  borderRight: "1px solid #dee2e6",
  verticalAlign: "middle",
  position: "sticky",
  left: 0,
  zIndex: 1,
};

const TD_CELL = {
  padding: 6,
  verticalAlign: "top",
  borderRight: "1px dashed #e9ecef",
};

const SchedulerGrid = ({ days, categories, tasks, cardColors, onTaskClick, CardComponent = TaskCard, isEmployee = false, categoryImages = {} }) => {
  if (!categories.length || !tasks.length) {
    return (
      <div style={{
        borderRadius: 12, border: "1px solid #e2e8f0", background: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "48px 20px",
        textAlign: "center", color: "#94a3b8", marginBottom: 24,
      }}>
        <i className="fa fa-calendar fa-2x mb-3 d-block" />
        <p style={{ margin: 0, fontSize: 13 }}>No assignments found for this week.</p>
      </div>
    );
  }

  return (
    <div style={{
      overflowX: "auto",
      borderRadius: 12,
      border: "1px solid #e2e8f0",
      background: "#fff",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      marginBottom: 24,
    }}>
      <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed", minWidth: 640 }}>
        <colgroup>
          <col style={{ width: 70 }} />
          {days.map((d) => <col key={d.full} />)}
        </colgroup>

        <thead>
          <tr>
            <th style={TH_CORNER} />
            {days.map((d) => (
              <th key={d.full} style={TH_DAY}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>{d.full}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{d.date}</div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {categories.filter((cat) => tasks.some((t) => t.category === cat)).map((cat) => {
            const catTasks = tasks.filter((t) => t.category === cat);
            const lanes    = buildLanes(catTasks);

            return lanes.map((lane, laneIdx) => (
              <tr
                key={`${cat}-lane-${laneIdx}`}
                style={{ borderBottom: "1px solid #dee2e6" }}
              >
                {/* Client avatar — image if available, initials as fallback */}
                <td style={{ ...TD_LABEL, width: 70 }} title={cat}>
                  <ClientAvatar name={cat} imageSrc={categoryImages[cat]} />
                </td>

                {lane.length === 0
                  /* empty category — fill 5 columns */
                  ? Array.from({ length: 5 }, (_, i) => (
                      <td key={i} style={TD_CELL} />
                    ))
                  : lane.map((cell) =>
                      cell.type === "empty" ? (
                        <td key={cell.key} colSpan={cell.span} style={TD_CELL} />
                      ) : (
                        <td key={cell.key} colSpan={cell.span} style={{ ...TD_CELL, minHeight: 64 }}>
                          <CardComponent
                            task={cell.task}
                            color={cardColors[cell.task.colorIndex]}
                            onClick={onTaskClick}
                            isEmployee={isEmployee}
                          />
                        </td>
                      )
                    )
                }
              </tr>
            ));
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SchedulerGrid;